import React, { useState, useEffect } from 'react';
import { Download, Search, ChevronRight, ChevronLeft } from 'lucide-react';
import ApiHandler from "./apiHandler";
import { API_BASE_URL } from "../Api/apiconnector";
import { useNavigate } from 'react-router-dom';

const Report  = () => {
  const [reports, setReports] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterInstitution, setFilterInstitution] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedReports, setSelectedReports] = useState([]);
  const [sending, setSending] = useState({}); // map of sending states per row, keys like `${id}-email` or `${id}-whatsapp`
  const itemsPerPage = 200;
  const [searchResults, setSearchResults] = useState(null); // null = no search, [] = search with no results   
  const [searchLoading, setSearchLoading] = useState(false);
  const navigator = useNavigate();
  const [darkMode, setDarkMode] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('pdfpage_darkmode')) || false;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('pdfpage_darkmode', JSON.stringify(darkMode));
    } catch {}
  }, [darkMode]);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ApiHandler.fetchCompletedTatCounters(itemsPerPage);
      console.log('Fetched data:', data);
      setReports((data.results || []).map(r => {
        // keep backward compatibility: if backend now returns report_url/report_title
        if (!Array.isArray(r.patient_reports)) {
          if (r.report_url) {
            return { ...r, patient_reports: [{ url: r.report_url, title: r.report_title || '' }] };
          }
          return { ...r, patient_reports: [] };
        }
        return r;
      }));
      setNextCursor(data.next_cursor);
    } catch (err) {
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreData = async () => {
    if (!nextCursor || loading) return;
    
    setLoading(true);
    try {
      const data = await ApiHandler.fetchCompletedTatCounters(itemsPerPage, nextCursor);
      setReports(prev => [...prev, ...((data.results || []).map(r => {
        if (!Array.isArray(r.patient_reports)) {
          if (r.report_url) {
            return { ...r, patient_reports: [{ url: r.report_url, title: r.report_title || '' }] };
          }
          return { ...r, patient_reports: [] };
        }
        return r;
      }))]);
      setNextCursor(data.next_cursor);
      setCurrentPage(prev => prev + 1);
    } catch (err) {
      setError('Failed to load more data.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  // Helper: normalize a single report object so the UI can always rely on
  // `patient_reports` being an array of { url, title } objects.
  // New backend shape: report_url, report_title at top-level.
  // Old backend shape: patient_reports: [{ url }]
  const normalizeReport = (r) => {
    if (Array.isArray(r.patient_reports)) return r;
    if (r.report_url) return { ...r, patient_reports: [{ url: r.report_url, title: r.report_title || '' }] };
    return { ...r, patient_reports: [] };
  };

  // Send report PDF by email. Uses endpoint: POST /api/email-pdf-raw/<patient_id>/ with payload { email, name, pdf_url }
  const sendEmail = async (report) => {
    const pdf = report.patient_reports?.[0]?.url;
    if (!pdf) {
      alert('No PDF available to send.');
      return;
    }
    const key = `${report.id}-email`;
    setSending(prev => ({ ...prev, [key]: true }));
    const payload = {
      email: report.email || '',
      name: report.patient_name || '',
      pdf_key: pdf,
    };
    try {
      const res = await ApiHandler.sendEmailPdfRaw(report.patient_id || '', payload);
      if (!res.success) throw new Error(res.error || 'Server error');
      alert('Email API triggered successfully.');
    } catch (err) {
      console.error('sendEmail error', err);
      alert('Failed to trigger email API. Check console for details.');
    } finally {
      setSending(prev => ({ ...prev, [key]: false }));
    }
  };


const handleSearch = async () => {
  if (!searchTerm.trim()) {
    setSearchResults(null);
    return;
  }

  setSearchLoading(true);
  setError(null); // Clear previous errors
  try {
    const result = await ApiHandler.searchReport(searchTerm);
    if (result.success) {
      // Normalize the search results to match the structure
      const normalizedResults = (result.data || []).map(r => {
        if (!Array.isArray(r.patient_reports)) {
          if (r.report_url) {
            return { ...r, patient_reports: [{ url: r.report_url, title: r.report_title || '' }] };
          }
          return { ...r, patient_reports: [] };
        }
        return r;
      });
      setSearchResults(normalizedResults);
    } else {
      setError('Search failed. Please try again.');
      setSearchResults([]);
    }
  } catch (err) {
    console.error('Search error:', err);
    setError('Search failed. Please try again.');
    setSearchResults([]);
  } finally {
    setSearchLoading(false);
  }
};

  // Send report PDF via WhatsApp. Uses endpoint: POST /api/send-whatsapp/ with payload { whatsapp_number, patient_name, pdf_url, patient_id }
  const sendWhatsapp = async (report) => {
    const pdf = report.patient_reports?.[0]?.url;
    if (!pdf) {
      alert('No PDF available to send.');
      return;
    }
    const key = `${report.id}-whatsapp`;
    setSending(prev => ({ ...prev, [key]: true }));
    const payload = {
      whatsapp_number: report.whatsapp_number || report.whatsapp || '',
      patient_name: report.patient_name || '',
      pdf_key: pdf,
      patient_id: report.patient_id || '',
    };
    try {
      const res = await ApiHandler.sendWhatsapp(payload);
      if (!res.success) throw new Error(res.error || 'Server error');
      alert('WhatsApp API triggered successfully.');
    } catch (err) {
      console.error('sendWhatsapp error', err);
      alert('Failed to trigger WhatsApp API. Check console for details.');
    } finally {
      setSending(prev => ({ ...prev, [key]: false }));
    }
  };

const downloadReport = async (fileKey, fileName) => {
  try {
    // First get the presigned URL
    const presignedResponse = await ApiHandler.getPresignedUrl(fileKey);
    console.log('Presigned URL response:', presignedResponse);
    if (!presignedResponse.success) {
      throw new Error(presignedResponse.error || 'Failed to get download URL');
    }
    
    // Download the file
    const response = await fetch(presignedResponse.url);
    console.log('File download response:', response);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName || 'report.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    
  } catch (err) {
    console.error('Download failed:', err);
    alert('Download failed: ' + err.message);
  }
};


  const handleDownloadAllReports = (report) => {
    if (report.patient_reports && report.patient_reports.length > 0) {
      report.patient_reports.forEach((rep, idx) => {
        setTimeout(() => {
          const fileName = `${report.patient_id || 'Patient'}_${report.patient_name || 'Unknown'}_Report_${idx + 1}.pdf`;
          downloadReport(rep.url, fileName);
        }, idx * 500);
      });
    }
  };

   const filteredReports = (() => {
     // If search results exist, use them instead of all reports
     const dataSource = searchResults !== null ? searchResults : reports;
     
     return dataSource.filter(report => {
       const matchesInstitution = filterInstitution === 'All' || 
         report.institution_name === filterInstitution;
       
       return matchesInstitution;
     });
   })();

  const uniqueInstitutions = ['All', ...new Set(reports.map(r => r.institution_name).filter(Boolean))];

  const handleSelectReport = (reportId) => {
    setSelectedReports(prev => {
      if (prev.includes(reportId)) {
        return prev.filter(id => id !== reportId);
      } else {
        return [...prev, reportId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedReports.length === filteredReports.length) {
      setSelectedReports([]);
    } else {
      setSelectedReports(filteredReports.map(r => r.id));
    }
  };

  const handleBulkDownload = async () => {
    if (selectedReports.length === 0) {
      alert('Please select at least one patient to download reports');
      return;
    }

    const selectedPatients = reports.filter(r => selectedReports.includes(r.id));
    
    let delay = 0;
    for (const patient of selectedPatients) {
      if (patient.patient_reports && patient.patient_reports.length > 0) {
        for (const report of patient.patient_reports) {
          setTimeout(() => {
            const fileName = `${patient.patient_id || 'Patient'}_${patient.patient_name || 'Unknown'}_Report.pdf`;
            downloadReport(report.url, fileName);
          }, delay);
          delay += 500;
        }
      }
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'}`}>
      {/* Header */}
      <header className={`px-6 py-4 flex justify-between items-center ${darkMode ? 'bg-gray-800 text-white border-b border-gray-700' : 'bg-white text-gray-900 border-b border-gray-200'}`}>
        <div className="flex items-center gap-4">
         
          <img
            src="https://u4rad.com/static/media/Logo.c9920d154c922ea9e355.png"
            alt="U4rad"
            style={{
              height: 50,
              backgroundColor: darkMode ? 'white' : 'transparent',
              borderRadius: 6,
              padding: 2
            }}
          />
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setDarkMode(!darkMode)}
            className={`px-3 py-2 rounded text-sm ${darkMode ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-700'}`}>
            {darkMode ? '☀️' : '🌙'}
          </button>
          <button
            onClick={() => navigator(-1)}
            title="Go back"
            className={`flex items-center justify-center w-25 h-10 rounded-md text-sm ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            <ChevronLeft size={18} />
            Go Back
          </button>
          <button onClick={() => navigator('/')} className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded text-sm text-white">
            Logout
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="p-6">
        {/* Stats and Controls */}
        <div className={`rounded-lg shadow mb-6 p-4 ${darkMode ? 'bg-gray-800 text-white border border-gray-700' : 'bg-white text-gray-900'}`}>
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div className="text-sm flex items-center gap-4">
             <span className="font-semibold">
               {searchResults !== null 
                 ? `Search Results: ${filteredReports.length}` 
                 : `Total XRAY Reports: ${reports.length}`
               }
             </span>
              {selectedReports.length > 0 && (
                <span className="text-blue-600 font-semibold">
                  {selectedReports.length} Selected
                </span>
              )}
            </div>
            
            <div className="flex gap-2 items-center flex-wrap">
              <input
                type="text"
                placeholder="Search by name or ID"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                className={`border rounded px-3 py-2 text-sm w-64 ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'
                }`}
              />
              <button 
                onClick={handleSearch}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm flex items-center gap-2"
              >
                <Search size={16} />
                Search
              </button>
              {searchResults !== null && (
                <button 
                  onClick={() => {
                    setSearchResults(null);
                    setSearchTerm('');
                  }}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm"
                >
                  Clear Search
                </button>
              )}
              <button 
                onClick={handleBulkDownload}
                disabled={selectedReports.length === 0}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm flex items-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <Download size={16} />
                Download Selected ({selectedReports.length})
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className={`rounded-lg shadow overflow-hidden ${darkMode ? 'bg-gray-800 text-white border border-gray-700' : 'bg-white text-gray-900'}`}>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 m-4 rounded">
              {error}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`${darkMode ? 'bg-gray-700 text-white border-b border-gray-600' : 'bg-gray-50 border-b'}`}>
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={filteredReports.length > 0 && selectedReports.length === filteredReports.length}
                      onChange={handleSelectAll}
                      className="w-4 h-4 cursor-pointer"
                    />
                  </th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Patient ID</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Name</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <select
                      value={filterInstitution}
                      onChange={(e) => setFilterInstitution(e.target.value)}
                      className={`border rounded px-2 py-1 text-xs ${
                        darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300 bg-white text-gray-900'
                      }`}
                    >
                      {uniqueInstitutions.map(inst => (
                        <option key={inst} value={inst}>{inst}</option>
                      ))}
                    </select>
                  </th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Study Date</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Modality</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Location</th>
                  {/* <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Radiologist</th> */}
                  <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>WhatsApp</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Email</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Actions</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Word Report</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Send Email</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Send WhatsApp</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Reports</th>
                </tr>
              </thead>
              <tbody className={darkMode ? 'divide-y divide-gray-700' : 'divide-y divide-gray-200'}>
                {(loading && reports.length === 0) || searchLoading ? (
                 <tr>
                   <td colSpan="14" className={`px-4 py-8 text-center ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                     {searchLoading ? 'Searching...' : 'Loading...'}
                   </td>
                 </tr>
                ) : filteredReports.length === 0 ? (
                <tr>
                  <td colSpan="14" className={`px-4 py-8 text-center ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {searchResults !== null ? 'No results found for your search' : 'No reports found'}
                  </td>
                </tr>
                ) : (
                  filteredReports.map((report) => (
                    <tr key={report.id} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedReports.includes(report.id)}
                          onChange={() => handleSelectReport(report.id)}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm">{report.patient_id || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm">{report.patient_name || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm">{report.institution_name || 'null'}</td>
                      <td className="px-4 py-3 text-sm">{report.study_date || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                          {report.modality || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">{report.location || 'N/A'}</td>
                      {/* <td className="px-4 py-3 text-sm">
                        {report.radiologist?.length > 0 ? report.radiologist.join(', ') : 'N/A'}
                      </td> */}
                      <td className="px-4 py-3 text-sm">{report.whatsapp_number || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm">{report.email || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm">
                       
                       <button 
                         onClick={async () => {
                           try {
                             // Use report.report_url directly since it's at the top level
                             const reportUrl = report.report_url || report.patient_reports?.[0]?.url;
                             
                             if (!reportUrl) {
                               alert('No report available');
                               return;
                             }
                             
                             const urlResult = await ApiHandler.getCompletePresignedUrl(reportUrl);
                             if (urlResult.success && urlResult.url) {
                               window.open(urlResult.url, '_blank');
                             } else {
                               alert('Failed to open report');
                             }
                           } catch (error) {
                             console.error('Error opening report:', error);
                             alert('Failed to open report');
                           }
                         }}
                         className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1 rounded text-xs"
                       >
                         DOWNLOAD
                       </button>

                      </td>
                      <td className="px-4 py-3 text-sm">
                      <button
                        onClick={async () => {
                          try {
                            if (!report.patient_reports || report.patient_reports.length === 0) {
                              alert("No report file URL found.");
                              return;
                            }
                      
                            const file_key = report.patient_reports[0].url;
                      
                            const response = await fetch(`${API_BASE_URL}/convert-pdf-to-word/?pdf_key=${file_key}`, {
                              method: "GET",
                              credentials: "include",
                            });
                      
                            if (!response.ok) throw new Error("Conversion failed");
                      
                            const blob = await response.blob();
                            const disposition = response.headers.get("content-disposition");
                            let filename = "Converted_Report.doc";
                            if (disposition && disposition.includes("filename=")) {
                              filename = disposition.split("filename=")[1].replace(/"/g, "");
                            }
                      
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = filename;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            window.URL.revokeObjectURL(url);
                          } catch (err) {
                            console.error("Word conversion error:", err);
                            alert("Error converting PDF to Word");
                          }
                        }}
                        className="flex items-center w-full gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 border-t border-gray-100"
                      >
                        📄 Convert to Word
                      </button>
                      </td>
                      
                      <td className="px-4 py-3 text-sm">
                        {report.patient_reports?.length > 0 ? (
                          <button
                            onClick={() => sendEmail(report)}
                            disabled={!report.patient_reports?.[0]?.url || sending[`${report.id}-email`]}
                            className={`px-3 py-1 rounded text-xs ${sending[`${report.id}-email`] ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}
                          >
                            {sending[`${report.id}-email`] ? 'Sending...' : 'Send Email'}
                          </button>
                        ) : (
                          'No PDF'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {report.patient_reports?.length > 0 ? (
                          <button
                            onClick={() => sendWhatsapp(report)}
                            disabled={!report.patient_reports?.[0]?.url || sending[`${report.id}-whatsapp`]}
                            className={`px-3 py-1 rounded text-xs ${sending[`${report.id}-whatsapp`] ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 text-white'}`}
                          >
                            {sending[`${report.id}-whatsapp`] ? 'Sending...' : 'Send WhatsApp'}
                          </button>
                        ) : (
                          'No PDF'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {report.patient_reports?.length > 0 ? (
                          <div className="flex flex-col gap-1">
                            {report.patient_reports.map((rep, idx) => (
                              <button
                                key={idx}
                                onClick={async () => {
                                  try {
                                   const urlResult = await ApiHandler.getPresignedUrl(report.report_url);
                                   if (urlResult.success && urlResult.url) {
                                     window.open(urlResult.url, '_blank');
                                   } else {
                                     alert('Failed to open report');
                                   }
                                 } catch (error) {
                                   console.error('Error opening report:', error);
                                   alert('Failed to open report');
                                 }
                                }
                              }
                                className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-1 rounded text-xs"
                              >

                                📥 Report {idx + 1}
                              </button>
                            ))}
                          </div>
                        ) : (
                          'No reports'
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Load More Button */}
          {nextCursor && searchResults === null && (
            <div className={`p-4 text-center ${darkMode ? 'border-t border-gray-700' : 'border-t'}`}>
              <button
                onClick={loadMoreData}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Loading...' : 'Show More'}
              </button>
            </div>
          )}
        </div>

        {/* Pagination Info */}
        <div className={`mt-4 text-sm text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Showing {filteredReports.length} of {reports.length} reports
          {nextCursor && ' (More available)'}
        </div>
      </div>
    </div>
  );
};

export default Report;